import type {
  ColorByCategory,
  ColorByOption,
} from 'components/color-by-attribute-modal';
import {
  WELL_STATUS_LABELS,
  type StagedWell,
  type WellStatusToken,
} from 'types/well-workspace';
import {
  WELLBORE_GEOMETRY_ATTRIBUTE,
  wellboreGeometryOptions,
} from 'components/wells-color-by';
import { US_DATE_FORMAT, parseMomentUtc } from 'utils/dates';

/**
 * The attributes a workspace can colour its wells by.
 *
 * Wider than the Well page's list because a workspace holds many wells, so grouping by
 * state, county or operator is meaningful. Limited to fields a staged well carries,
 * which leaves out the mock's three recency buckets: two need an "added" timestamp
 * StagedWell has none of, and the third is a bucket rather than a value.
 */
export const WORKSPACE_COLOR_ATTRIBUTES: ColorByCategory[] = [
  { id: 'uwi', label: 'UWI' },
  { id: 'status', label: 'Well Status' },
  { id: 'holeDirection', label: 'Hole Direction' },
  { id: 'currentOperator', label: 'Current Operator' },
  { id: 'permitDate', label: 'Permit Date' },
  { id: 'spudDate', label: 'Spud Date' },
  { id: 'firstProductionDate', label: 'First Production Date' },
  { id: 'stateAbbreviation', label: 'State' },
  { id: 'county', label: 'County' },
  { id: 'srpLandingZone', label: 'SIM Landing Zone' },
  { id: 'vendorLandingZone', label: 'Vendor Landing Zone' },
  { id: WELLBORE_GEOMETRY_ATTRIBUTE, label: 'Wellbore Geometry' },
];

/** The attributes whose values are dates, shown as dates rather than raw timestamps. */
const DATE_ATTRIBUTES = new Set([
  'permitDate',
  'spudDate',
  'firstProductionDate',
]);

// A date value the reader can pick a colour for. Falls back to the raw string when the
// value will not parse, so an unexpected format shows as itself rather than "Invalid
// date" — and the rule still matches, because the id is the raw value either way.
const formatDate = (value: string): string => {
  const parsed = parseMomentUtc(value);
  return parsed.isValid() ? parsed.format(US_DATE_FORMAT) : value;
};

/**
 * The distinct values of one attribute across the workspace's wells.
 *
 * Read off the workspace's own wells rather than fetched, so the rules offer the values
 * its wells actually have. Blanks are dropped, and a status shows its label while
 * keeping the token as its id so a rule matches what the surfaces paint on.
 * @param {StagedWell[]} wells The workspace's staged wells.
 * @param {string} attributeId The attribute to read.
 * @return {ColorByOption[]} Its distinct values, ordered for reading.
 */
export const workspaceColorOptions = (
  wells: StagedWell[],
  attributeId: string
): ColorByOption[] => {
  // A fixed vocabulary of trace kinds, not a field these wells carry a value for.
  if (attributeId === WELLBORE_GEOMETRY_ATTRIBUTE) {
    return wellboreGeometryOptions();
  }

  const seen = new Map<string, string>();

  wells.forEach((well) => {
    const raw = (well as unknown as Record<string, unknown>)[attributeId];
    if (raw === null || raw === undefined || raw === '') return;

    const id = String(raw);
    if (seen.has(id)) return;

    if (attributeId === 'status') {
      seen.set(id, WELL_STATUS_LABELS[id as WellStatusToken] ?? id);
    } else if (DATE_ATTRIBUTES.has(attributeId)) {
      // Keyed on the raw value, which is what a rule matches on; shown as a date, since
      // an ISO timestamp is not something a reader picks a colour for.
      seen.set(id, formatDate(id));
    } else {
      seen.set(id, id);
    }
  });

  // Dates read newest-first; everything else alphabetically.
  const options = [...seen.entries()].map(([id, name]) => ({ id, name }));
  if (DATE_ATTRIBUTES.has(attributeId)) {
    return options.sort((left, right) => right.id.localeCompare(left.id));
  }

  return options.sort((left, right) => left.name.localeCompare(right.name));
};
